import React from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import PendingVerificationTab from "./PendingVerificationTab";
import PlatformOrgInvitationsTab from "./PlatformOrgInvitationsTab";
import PlatformUserInvitationsTab from "./PlatformUserInvitationsTab";
import AdminSurveysTab from "./AdminSurveysTab";
import { AdminUsersTab, AdminOrganizationsTab } from "./AdminPlaceholders";
import AdminProfileDetail from "./AdminProfileDetail";

/**
 * Main platform admin dashboard shell.
 * @param {{admin: {id:number, username:string}}} props
 * @returns {JSX.Element}
 */
function AdminDashboard({ admin }) {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const tabs = [
    { path: "pending", label: "Pending verification" },
    { path: "invitations", label: "Org invitations" },
    { path: "individual-invitations", label: "Individual invitations" },
    { path: "surveys", label: "Surveys" },
    { path: "public-feedback", label: "Public feedback" },
    { path: "users", label: "Users" },
    { path: "organizations", label: "Organizations" },
  ];

  const pageEnter = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 6 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] },
      };

  /**
   * Checks whether the admin sidebar tab is active.
   * @param {string} path
   * @returns {boolean}
   */
  function isActiveTab(path) {
    if (location.pathname === "/platform-admin" && path === "pending") {
      return true;
    }
    return (
      location.pathname === `/platform-admin/${path}` ||
      location.pathname.startsWith(`/platform-admin/${path}/`)
    );
  }

  return (
    <div className="bg-ui-background min-h-screen">
      <motion.div className="mx-auto max-w-7xl px-4 py-6 sm:py-8" {...pageEnter}>
        <h1 className="mb-1 text-2xl font-bold tracking-tight text-text-primary sm:text-3xl">
          Assembly admin
        </h1>
        <p className="mb-6 text-sm text-text-secondary sm:mb-8">
          Signed in as <span className="font-semibold text-text-primary">@{admin.username}</span>
        </p>

        <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
          <aside className="w-full shrink-0 lg:w-60">
            <Card className="overflow-hidden p-1 shadow-profile-sm">
              <nav className="flex flex-col gap-0.5" aria-label="Platform admin sections">
                {tabs.map((tab) => {
                  const active = isActiveTab(tab.path);
                  return (
                    <Button
                      key={tab.path}
                      asChild
                      variant={active ? "default" : "ghost"}
                      className={cn(
                        "h-auto w-full justify-start px-3 py-2.5 text-left font-normal",
                        !active && "text-text-secondary"
                      )}
                    >
                      <Link to={`/platform-admin/${tab.path}`}>{tab.label}</Link>
                    </Button>
                  );
                })}
              </nav>
            </Card>
          </aside>

          <div className="min-w-0 flex-1">
            <Card className="min-h-[12rem] shadow-profile-sm">
              <CardContent className="p-4 sm:p-6">
                <Routes>
                  <Route path="/" element={<Navigate to="/platform-admin/pending" replace />} />
                  <Route path="pending" element={<PendingVerificationTab adminId={admin.id} />} />
                  <Route path="invitations" element={<PlatformOrgInvitationsTab adminId={admin.id} />} />
                  <Route
                    path="individual-invitations"
                    element={<PlatformUserInvitationsTab adminId={admin.id} />}
                  />
                  <Route path="surveys" element={<AdminSurveysTab adminId={admin.id} />} />
                  <Route path="surveys/:surveyId" element={<AdminSurveysTab adminId={admin.id} />} />
                  <Route
                    path="surveys/:surveyId/:responseId"
                    element={<AdminSurveysTab adminId={admin.id} />}
                  />
                  <Route
                    path="public-feedback"
                    element={
                      <AdminSurveysTab
                        adminId={admin.id}
                        audience="public"
                        basePath="/platform-admin/public-feedback"
                      />
                    }
                  />
                  <Route
                    path="public-feedback/:surveyId"
                    element={
                      <AdminSurveysTab
                        adminId={admin.id}
                        audience="public"
                        basePath="/platform-admin/public-feedback"
                      />
                    }
                  />
                  <Route
                    path="public-feedback/:surveyId/:responseId"
                    element={
                      <AdminSurveysTab
                        adminId={admin.id}
                        audience="public"
                        basePath="/platform-admin/public-feedback"
                      />
                    }
                  />
                  <Route path="users" element={<AdminUsersTab adminId={admin.id} />} />
                  <Route
                    path="users/:accountId"
                    element={<AdminProfileDetail adminId={admin.id} mode="users" />}
                  />
                  <Route
                    path="organizations"
                    element={<AdminOrganizationsTab adminId={admin.id} />}
                  />
                  <Route
                    path="organizations/:accountId"
                    element={<AdminProfileDetail adminId={admin.id} mode="organizations" />}
                  />
                </Routes>
              </CardContent>
            </Card>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default AdminDashboard;
